import React from 'react';
import { Tabs as ArkTabs } from '@ark-ui/react';
import { Button } from '../Button/Button';
import styles from './Tabs.module.scss';
import clsx from 'clsx';

export interface TabItem {
  id: string;
  label: string;
  content?: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

export const Tabs = ({
  items,
  defaultValue,
  value,
  onValueChange,
  className,
}: TabsProps) => {
  const [internalValue, setInternalValue] = React.useState(
    value ?? defaultValue ?? items[0]?.id ?? '',
  );

  React.useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  // When items change in uncontrolled mode (e.g. populated after async load),
  // re-sync internalValue if the current value is no longer a valid tab id.
  React.useEffect(() => {
    if (value === undefined) {
      const isValid = items.some((item) => item.id === internalValue);
      if (!isValid && items.length > 0) {
        setInternalValue(defaultValue ?? items[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, defaultValue, value]);

  const handleValueChange = (newValue: string) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  const rootProps =
    value !== undefined
      ? { value }
      : { defaultValue: defaultValue ?? items[0]?.id ?? '' };

  if (items.length === 0) {
    return null;
  }

  return (
    <ArkTabs.Root
      {...rootProps}
      onValueChange={(e) => handleValueChange(e.value)}
      className={clsx(styles.tabsContext, className)}
    >
      <ArkTabs.List className={styles.tabList}>
        {items.map((item) => (
          <ArkTabs.Trigger
            key={item.id}
            value={item.id}
            disabled={item.disabled}
            asChild
          >
            <Button
              variant={internalValue === item.id ? 'tertiary' : 'outline'}
              disabled={item.disabled}
              data-testid={`tab-${item.id}`}
            >
              {item.label}
            </Button>
          </ArkTabs.Trigger>
        ))}
      </ArkTabs.List>
      {items.map(
        (item) =>
          item.content && (
            <ArkTabs.Content key={item.id} value={item.id}>
              {item.content}
            </ArkTabs.Content>
          ),
      )}
    </ArkTabs.Root>
  );
};
